export const COMPANY_MODULE_ACCESS_TAG = 'company-module-access'

export type CompanyModuleAccessKey =
  | 'registration'
  | 'transfer'
  | 'workspace'
  | 'api'
  | 'spcSearch'
  | 'documentsCollect'
  | 'lawsuits'
  | 'documentsReads'
  | 'externalCollection'
  | 'lawsuitsAutos'
  | 'reports'
  | 'defaultReport'
  | 'customReport'
  | 'smartFill'

export type CompanyModuleAccessConfig = Record<CompanyModuleAccessKey, boolean>

export type CompanyAccessConfiguration = {
  name?: string
  tag?: string
  configuration?: Record<string, boolean>
}

export const COMPANY_MODULE_ACCESS_GROUPS: Array<{
  key: CompanyModuleAccessKey
  label: string
  description?: string
  icon?: string
  children?: Array<{
    key: CompanyModuleAccessKey
    label: string
  }>
}> = [
  {
    key: 'registration',
    label: 'Leitura de Matrícula',
    description: 'Gestão de novos cadastros e leitura de matrícula.',
    icon: 'mdi:mdi-card-account-details-outline'
  },
  {
    key: 'transfer',
    label: 'Repasse',
    description: 'Acesso ao fluxo de repasse e análise da operação.',
    icon: 'mdi:mdi-swap-horizontal-bold'
  },
  {
    key: 'workspace',
    label: 'Workspace',
    description:
      'Acesso ao ambiente colaborativo de comentários e acompanhamento.',
    icon: 'mdi:mdi-message-badge-outline'
  },
  {
    key: 'api',
    label: 'API',
    description: 'Acesso às funcionalidades de integração e chaves de API.',
    icon: 'mdi:mdi-api'
  },
  {
    key: 'spcSearch',
    label: 'Consulta SPC',
    description: 'Acesso à análise de crédito via consulta SPC.',
    icon: 'mdi:mdi-finance'
  },
  {
    key: 'documentsCollect',
    label: 'Coleta de documentos',
    description:
      'Fluxo de recebimento, coleta e validação de arquivos digitais.',
    icon: 'mdi:mdi-file-document-multiple-outline',
    children: [
      { key: 'documentsReads', label: 'Leitura de documentos' },
      { key: 'externalCollection', label: 'Coleta externa' }
    ]
  },
  {
    key: 'lawsuits',
    label: 'Processos',
    description: 'Coleta e análise de processos judiciais.',
    icon: 'mdi:mdi-scale-balance',
    children: [{ key: 'lawsuitsAutos', label: 'Autos do processo' }]
  },
  {
    key: 'reports',
    label: 'Relatórios',
    description: 'Acesso a relatórios analíticos e exportação de dados.',
    icon: 'mdi:mdi-chart-box-outline',
    children: [
      { key: 'defaultReport', label: 'Relatório padrão' },
      { key: 'customReport', label: 'Relatório customizado' }
    ]
  },
  {
    key: 'smartFill',
    label: 'Preenchimento Inteligente',
    description:
      'Automação assistida para preenchimento e revisão inteligente.',
    icon: 'mdi:mdi-auto-fix'
  }
]

export const DEFAULT_COMPANY_MODULE_ACCESS: CompanyModuleAccessConfig = {
  registration: true,
  transfer: true,
  workspace: true,
  api: true,
  spcSearch: false,
  documentsCollect: true,
  documentsReads: true,
  externalCollection: true,
  lawsuits: true,
  lawsuitsAutos: true,
  reports: true,
  defaultReport: true,
  customReport: true,
  smartFill: true
}

const PARENT_ACCESS_MAP: Partial<
  Record<CompanyModuleAccessKey, CompanyModuleAccessKey>
> = {
  documentsReads: 'documentsCollect',
  externalCollection: 'documentsCollect',
  lawsuitsAutos: 'lawsuits',
  defaultReport: 'reports',
  customReport: 'reports'
}

const ROUTE_MODULE_MAP: Partial<Record<string, CompanyModuleAccessKey>> = {
  Diligence: 'registration',
  Entity: 'registration',
  EntityDocs: 'registration',
  EntitiesQSA: 'registration',
  RegistrationAnalysis: 'registration',
  BatchEntities: 'registration',
  ReviewBatchEntities: 'registration',
  Transfer: 'transfer',
  Workspace: 'workspace',
  ApiKeys: 'api',
  SpcSearch: 'spcSearch',
  DiligenceDocs: 'documentsCollect',
  Documents: 'documentsCollect',
  LawSuits: 'lawsuits',
  DiligenceLawSuits: 'lawsuits',
  Dossier: 'lawsuitsAutos',
  ExternalUploadSettings: 'externalCollection',
  DocumentAnalysis: 'documentsReads',
  PersonalDocumentAnalysis: 'documentsReads',
  DocumentDetails: 'documentsReads',
  DocumentList: 'documentsReads',
  DocumentProcessor: 'documentsReads',
  DiligenceStandardReport: 'defaultReport',
  DiligenceCustomReport: 'customReport',
  SmartFillRunProcessing: 'smartFill',
  SmartFillReview: 'smartFill'
}

export const getCompanyModuleAccess = (
  accessConfigurations?: CompanyAccessConfiguration[]
): CompanyModuleAccessConfig => {
  const accessConfig = accessConfigurations?.find(
    (item) => item?.tag === COMPANY_MODULE_ACCESS_TAG
  )

  const config = accessConfig?.configuration || {}

  return {
    ...DEFAULT_COMPANY_MODULE_ACCESS,
    ...config
  }
}

export const upsertCompanyModuleAccessConfiguration = (
  accessConfigurations: CompanyAccessConfiguration[] = [],
  moduleAccess: CompanyModuleAccessConfig
): CompanyAccessConfiguration[] => {
  const sanitizedConfigurations = accessConfigurations.filter(
    (item) => item?.tag !== COMPANY_MODULE_ACCESS_TAG
  )

  sanitizedConfigurations.push({
    name: 'Company module access',
    tag: COMPANY_MODULE_ACCESS_TAG,
    configuration: {
      ...moduleAccess
    }
  })

  return sanitizedConfigurations
}

export const resolveCompanyModuleAccessKey = (
  routeName?: string | null
): CompanyModuleAccessKey | null => {
  if (!routeName) return null
  return ROUTE_MODULE_MAP[routeName] || null
}

export const hasCompanyModuleAccess = (
  moduleAccess: CompanyModuleAccessConfig,
  key: CompanyModuleAccessKey
): boolean => {
  const parentKey = PARENT_ACCESS_MAP[key]
  if (parentKey && !moduleAccess[parentKey]) return false
  return Boolean(moduleAccess[key])
}
